import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useResize } from 'src/hooks/shared-hooks';
import css from './accessibility-settings.module.scss';
import { fireEvent } from 'src/utils/analytics/google';
import { persistAccessibility, readAccessibility, setHtmlFontSize } from '../helper';
import Modal from 'src/components/common/modal/modal';
import { PopupProps } from 'src/components/layout/modals/search-popup';
import { ReactComponent as CloseCross } from 'src/assets/close_cross_v2.svg';
import { ReactComponent as ColorIcon } from 'src/assets/general/colored_icon_24px.svg';
import { ReactComponent as BlackWhiteIcon } from 'src/assets/general/black_white_icon_24px.svg';
import classNames from 'classnames';
import { MenuCategory } from 'src/types';

function AccessibilitySettings({ onOpen, onClose, trigger }: Readonly<PopupProps<MenuCategory>>) {
  const minFontSize = 12;
  const maxFontSize = 18;
  const allowedFontSizes = useMemo(() => [minFontSize, 14, maxFontSize], []);

  const persistedAccessibility = readAccessibility(minFontSize);

  const [isAccessibilityModeOn, setIsAccessibilityModeOn] = useState(persistedAccessibility.isAccessibilityMode);
  const [selectedFontSizeIndex, setSelectedFontSizeIndex] = useState(
    allowedFontSizes.indexOf(persistedAccessibility.fontSize) ?? 0
  );
  const htmlRef = useRef(document.getElementsByTagName('html')[0]);

  const dimensions = useResize();
  const desktopMode = dimensions.width > 1000;

  const switchAccessibilityModeOn = useCallback(() => {
    htmlRef.current.style.filter = 'grayscale(1)';
    htmlRef.current.setAttribute('accessibility-mode', 'true');
    fireEvent('accessibility_mode_on');
  }, []);

  const switchAccessibilityModeOff = () => {
    htmlRef.current.style.removeProperty('filter');
    htmlRef.current.removeAttribute('accessibility-mode');
  };

  const setCheckedFontSize = useCallback(
    (checkedIndex: number, checkPredicate: (n: number) => boolean) => {
      if (checkPredicate(checkedIndex)) return;
      htmlRef.current.setAttribute('accessibility-mode', `${checkedIndex !== 0 && !isAccessibilityModeOn}`);
      setSelectedFontSizeIndex(checkedIndex);
    },
    [isAccessibilityModeOn]
  );

  const handleFontSizeSmallerOnClick = () =>
    setCheckedFontSize(selectedFontSizeIndex - 1, (index: number) => index < 0 || !desktopMode);

  const handleFontSizeLargerOnClick = () =>
    setCheckedFontSize(
      selectedFontSizeIndex + 1,
      (index: number) => index > allowedFontSizes.length - 1 || !desktopMode
    );

  const clear = () => {
    setIsAccessibilityModeOn(false);
    setSelectedFontSizeIndex(0);
  };

  useEffect(() => {
    persistAccessibility(isAccessibilityModeOn, allowedFontSizes[selectedFontSizeIndex]);
  }, [allowedFontSizes, isAccessibilityModeOn, selectedFontSizeIndex]);

  useEffect(() => {
    isAccessibilityModeOn ? switchAccessibilityModeOn() : switchAccessibilityModeOff();
    if (selectedFontSizeIndex !== 0) {
      htmlRef.current.setAttribute('accessibility-mode', `true`);
    }
  }, [isAccessibilityModeOn, selectedFontSizeIndex, switchAccessibilityModeOn]);

  useEffect(() => {
    const fontSize = allowedFontSizes[selectedFontSizeIndex];
    setHtmlFontSize(fontSize);
  }, [allowedFontSizes, selectedFontSizeIndex]);

  const disabledClear = !(selectedFontSizeIndex !== 0 || isAccessibilityModeOn);

  return (
    <Modal onOpen={onOpen} trigger={trigger} onClose={onClose}>
      {() => (
        <div className={css.accPopup}>
          <div className={css.container}>
            <div className={css.accessibilityMode}>
              {desktopMode && (
                <div className={css.controls}>
                  <h3 className={css.title}>Розмір тексту</h3>
                  <div className={css.controlsBtns}>
                    <button
                      className={classNames(css.smaller, { [css.disabled]: selectedFontSizeIndex === 0 })}
                      onClick={handleFontSizeSmallerOnClick}>
                      -
                    </button>
                    <button
                      className={classNames(css.larger, {
                        [css.disabled]: selectedFontSizeIndex === allowedFontSizes.length - 1
                      })}
                      onClick={handleFontSizeLargerOnClick}>
                      +
                    </button>
                  </div>
                </div>
              )}
              <div className={css.colorSettings}>
                <h3 className={css.title}>Колір</h3>
                <div className={css.controlsBtns}>
                  <button
                    className={classNames(css.colorBtn, {
                      [css.disabled]: !isAccessibilityModeOn
                    })}
                    onClick={() => setIsAccessibilityModeOn(false)}>
                    <ColorIcon /> Колір
                  </button>
                  <button
                    className={classNames(css.colorBtn, {
                      [css.disabled]: isAccessibilityModeOn
                    })}
                    onClick={() => setIsAccessibilityModeOn(true)}>
                    <BlackWhiteIcon /> ЧБ
                  </button>
                </div>
              </div>

              <button className={classNames(css.switch, { [css.disabled]: disabledClear })} onClick={clear}>
                Скинути <CloseCross />
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default AccessibilitySettings;
